import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Post } from '@/types'
import type { TocItem } from '@/lib/toc'

import Link from 'next/link'
import { BadgeCheck, Info, Tag } from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { Breadcrumbs } from '@/components/generals/breadcrumbs'
import { JsonLd } from '@/components/generals/json-ld'
import { Badge } from '@/components/ui/badge'
import { AuthorAvatar, AuthorByline } from '@/components/blog/author-byline'
import { ArticleBody } from '@/components/blog/article-body'
import { TableOfContents } from '@/components/blog/table-of-contents'
import { PostCard } from '@/components/blog/post-card'

import { tocFor } from '@/lib/toc'
import { getPostBySlug, getRelatedPosts } from '@/lib/db'
import { articleSchema, breadcrumbSchema, graph } from '@/lib/structured-data'

interface PostPageProps {
    post: Post
    related: Post[]
    toc: TocItem[]
}

const PostPage: FC<PostPageProps> = ({ post, related, toc }): ReactNode => {
    return (
        <Layout>
            <Seo
                title={post.title}
                description={post.excerpt}
                path={`/${post.hubSlug}/${post.slug}`}
                ogImage={post.coverImage ?? undefined}
                imageAlt={post.title}
                type='article'
                publishedTime={post.publishedAt}
                modifiedTime={post.updatedAt ?? post.publishedAt}
                section={post.hubName}
                tags={post.tags}
                authorName={post.authorName}
            />

            <JsonLd
                data={graph([
                    breadcrumbSchema([
                        { name: 'Home', path: '/' },
                        { name: post.hubName, path: `/${post.hubSlug}` },
                        { name: post.title }
                    ]),
                    articleSchema(post)
                ])}
            />

            <article>
                {/* Header */}
                <Container className='pt-8'>
                    <Breadcrumbs
                        items={[
                            { label: 'Home', href: '/' },
                            { label: post.hubName, href: `/${post.hubSlug}` },
                            { label: post.title }
                        ]}
                    />

                    <div className='mx-auto mt-8 max-w-3xl'>
                        <Link href={`/${post.hubSlug}`}>
                            <Badge variant='primary'>{post.hubName}</Badge>
                        </Link>

                        <h1 className='mt-4 font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-foreground sm:text-4xl'>
                            {post.title}
                        </h1>

                        <p className='mt-5 text-lg leading-relaxed text-muted-foreground'>
                            {post.excerpt}
                        </p>

                        <div className='mt-7 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6'>
                            <AuthorByline
                                name={post.authorName}
                                role={post.authorRole}
                                date={post.publishedAt}
                                readMinutes={post.readMinutes}
                            />

                            {post.reviewedBy && (
                                <span className='inline-flex items-center gap-1.5 text-xs text-muted-foreground'>
                                    <BadgeCheck className='size-4 text-primary' />
                                    Medically reviewed by {post.reviewedBy}
                                </span>
                            )}
                        </div>
                    </div>
                </Container>

                {/* Cover */}
                <Container className='mt-10'>
                    <div className='mx-auto max-w-4xl overflow-hidden rounded-2xl border border-border'>
                        <img
                            src={post.coverImage ?? '/images/hero-smile.jpg'}
                            alt={post.title}
                            loading='eager'
                            decoding='async'
                            className='aspect-[16/9] w-full object-cover'
                        />
                    </div>
                </Container>

                {/* Body + TOC */}
                <Container className='mt-12'>
                    <div className='mx-auto grid max-w-4xl gap-10 lg:grid-cols-[minmax(0,1fr)_200px]'>
                        <div className='min-w-0'>
                            <ArticleBody content={post.content ?? ''} />

                            {post.tags.length > 0 && (
                                <div className='mt-10 flex flex-wrap items-center gap-2'>
                                    <Tag className='size-4 text-muted-foreground' />
                                    {post.tags.map(tag => (
                                        <Badge key={tag} variant='muted'>{tag}</Badge>
                                    ))}
                                </div>
                            )}

                            {post.authorName && (
                                <div className='mt-12 flex gap-4 rounded-xl border border-border bg-secondary/40 p-6'>
                                    <AuthorAvatar name={post.authorName} className='size-14 text-base' />

                                    <div>
                                        <div className='font-heading font-semibold text-foreground'>
                                            {post.authorName}
                                        </div>
                                        <div className='text-sm font-medium text-primary'>
                                            {post.authorRole}
                                            {post.authorCredentials ? ` · ${post.authorCredentials}` : ''}
                                        </div>
                                        {post.authorBio && (
                                            <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                                                {post.authorBio}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className='mt-8 flex gap-3 rounded-lg border border-border bg-card p-5'>
                                <Info className='size-5 shrink-0 text-primary' />
                                <p className='text-xs leading-relaxed text-muted-foreground'>
                                    This article is for general information only and is not a substitute for
                                    professional dental advice. Always consult your dentist about your
                                    individual needs.
                                </p>
                            </div>
                        </div>

                        <aside className='hidden lg:block'>
                            <div className='sticky top-24'>
                                <TableOfContents items={toc} />
                            </div>
                        </aside>
                    </div>
                </Container>
            </article>

            {/* Related */}
            {related.length > 0 && (
                <section className='mt-20 border-t border-border bg-secondary/30'>
                    <Container className='py-16'>
                        <h2 className='font-heading text-2xl font-bold tracking-tight text-foreground'>
                            More from {post.hubName}
                        </h2>

                        <div className='mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                            {related.map(item => (
                                <PostCard key={item.slug} post={item} />
                            ))}
                        </div>
                    </Container>
                </section>
            )}
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<PostPageProps> = async context => {
    const hubSlug = context.params?.hub as string
    const slug = context.params?.slug as string

    const post = await getPostBySlug(slug)

    if (!post || post.hubSlug !== hubSlug) {
        return { notFound: true }
    }

    const related = await getRelatedPosts(post.hubSlug, post.slug, 3)
    const toc = tocFor(post.content ?? '')

    return {
        props: { post, related, toc }
    }
}

export default PostPage
